import { makeStyles } from '@mui/styles';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  TextField,
  Select,
  InputLabel,
  MenuItem,
  Divider,
  Typography,
  Box,
  Switch,
} from '@mui/material';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';

import { noWheelInputProps } from 'util/utils';
const useStyles = makeStyles((theme) => ({
  formControl: {
    marginBottom: theme.spacing(2),
    minWidth: 200,
  },
  batchSection: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.grey[50],
    borderRadius: theme.shape.borderRadius,
  },
  tableRangeInputs: {
    display: 'flex',
    gap: theme.spacing(2),
    alignItems: 'center',
  },
  generateButton: {
    marginTop: theme.spacing(2),
  },
}));

const ORDER_TYPES = [
  { value: 'dinein', label: 'Dine-in' },
  { value: 'delivery', label: 'Delivery' },
  { value: 'takeaway', label: 'Takeaway' },
  { value: 'pickup', label: 'Pickup' },
  { value: 'other', label: 'Other' },
];

const PAYMENT_OPTIONS = [
  { value: 'required', label: 'Required' },
  { value: 'not_required', label: 'Not Required' },
  { value: 'optional', label: 'Optional' },
];

const EXPIRE_TIME_OPTIONS = [
  { value: 0, label: 'Never' },
  { value: 1, label: '1 hour' },
  { value: 2, label: '2 hours' },
  { value: 4, label: '4 hours' },
  { value: 8, label: '8 hours' },
  { value: 24, label: '24 hours' },
  { value: 48, label: '48 hours' },
  { value: 168, label: '1 week' },
];

const POS_ACCEPT_OPTIONS = [
  { value: 'automatic', label: 'Automatically' },
  { value: 'manual', label: 'Manual' },
];

const QrCodeEditDialog = ({
  open,
  onClose,
  editOrderType,
  setEditOrderType,
  editPayment,
  setEditPayment,
  editExpireTime,
  setEditExpireTime,
  editPosAcceptOrders,
  setEditPosAcceptOrders,
  editDisabled,
  setEditDisabled,
  startTableNumber,
  setStartTableNumber,
  endTableNumber,
  setEndTableNumber,
  uiLoading,
  pdfProgress,
  onSave,
  onGenerateBatchPDF,
}) => {
  const classes = useStyles();

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>Edit QR Code Settings</DialogTitle>
      <DialogContent>
        <Box mt={2}>
          {/* Order Type */}
          <FormControl component="fieldset" className={classes.formControl} fullWidth disabled={uiLoading}>
            <FormLabel component="legend">Order Type</FormLabel>
            <RadioGroup
              row
              value={editOrderType}
              onChange={(e) => setEditOrderType(e.target.value)}
            >
              {ORDER_TYPES.map((option) => (
                <FormControlLabel
                  key={option.value}
                  value={option.value}
                  control={<Radio />}
                  label={option.label}
                />
              ))}
            </RadioGroup>
          </FormControl>

          <Divider style={{ margin: '16px 0' }} />

          {/* Payment */}
          <FormControl component="fieldset" className={classes.formControl} fullWidth disabled={uiLoading}>
            <FormLabel component="legend">Payment</FormLabel>
            <RadioGroup
              row
              value={editPayment}
              onChange={(e) => setEditPayment(e.target.value)}
            >
              {PAYMENT_OPTIONS.map((option) => (
                <FormControlLabel
                  key={option.value}
                  value={option.value}
                  control={<Radio />}
                  label={option.label}
                />
              ))}
            </RadioGroup>
          </FormControl>

          <Divider style={{ margin: '16px 0' }} />

          {/* Expire Time */}
          <FormControl fullWidth className={classes.formControl} disabled={uiLoading}>
            <InputLabel id="expire-time-label">Expire Time</InputLabel>
            <Select
              labelId="expire-time-label"
              value={editExpireTime}
              label="Expire Time"
              onChange={(e) => setEditExpireTime(e.target.value)}
            >
              {EXPIRE_TIME_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Divider style={{ margin: '16px 0' }} />

          {/* POS Accept Orders */}
          <FormControl component="fieldset" className={classes.formControl} fullWidth disabled={uiLoading}>
            <FormLabel component="legend">POS Accept Orders</FormLabel>
            <RadioGroup
              row
              value={editPosAcceptOrders}
              onChange={(e) => setEditPosAcceptOrders(e.target.value)}
            >
              {POS_ACCEPT_OPTIONS.map((option) => (
                <FormControlLabel
                  key={option.value}
                  value={option.value}
                  control={<Radio />}
                  label={option.label}
                />
              ))}
            </RadioGroup>
          </FormControl>

          <Divider style={{ margin: '16px 0' }} />

          {/* Disabled Status */}
          <FormControl component="fieldset" className={classes.formControl} fullWidth disabled={uiLoading}>
            <FormLabel component="legend">QR Code Status</FormLabel>
            <FormControlLabel
              control={
                <Switch
                  checked={!editDisabled}
                  onChange={(e) => setEditDisabled(!e.target.checked)}
                  color="primary"
                />
              }
              label={editDisabled ? 'Disabled (QR code is inactive)' : 'Enabled (QR code is active)'}
            />
            <Typography variant="caption" color="textSecondary" style={{ marginTop: 4 }}>
              When disabled, this QR code cannot be used to place orders
            </Typography>
          </FormControl>

          <Divider style={{ margin: '16px 0' }} />

          {/* Batch Generation Section */}
          <Box className={classes.batchSection}>
            <Typography variant="subtitle1" gutterBottom>
              Batch Generate Table QR Codes (PDF)
            </Typography>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              Generate multiple table QR codes with shortened URLs in a single PDF file
            </Typography>

            <Box className={classes.tableRangeInputs} mt={2}>
              <TextField
                label="Start Table"
                type="number"
                variant="outlined"
                size="small"
                value={startTableNumber}
                onChange={(e) => setStartTableNumber(e.target.value)}
                inputProps={{ ...noWheelInputProps, min: 1 }}
                style={{ width: 120 }}
                disabled={uiLoading}
              />
              <Typography>to</Typography>
              <TextField
                label="End Table"
                type="number"
                variant="outlined"
                size="small"
                value={endTableNumber}
                onChange={(e) => setEndTableNumber(e.target.value)}
                inputProps={{ ...noWheelInputProps, min: 1 }}
                style={{ width: 120 }}
                disabled={uiLoading}
              />
            </Box>

            {startTableNumber && endTableNumber && (
              <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>
                Will generate {Math.max(0, parseInt(endTableNumber, 10) - parseInt(startTableNumber, 10) + 1) || 0} QR codes
              </Typography>
            )}

            <Button
              variant="contained"
              color="secondary"
              fullWidth
              startIcon={<PictureAsPdfIcon />}
              className={classes.generateButton}
              onClick={onGenerateBatchPDF}
              disabled={uiLoading || !startTableNumber || !endTableNumber}
            >
              {uiLoading && pdfProgress > 0
                ? `Creating short URLs & PDF... ${pdfProgress}%`
                : 'Generate PDF with Short URLs'}
            </Button>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>
          Cancel
        </Button>
        <Button 
          onClick={onSave} 
          color="primary" 
          variant="contained"
          disabled={uiLoading}
        >
          Save & Update QR Code
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default QrCodeEditDialog;
